import { Flex, Skeleton } from '@backstage/ui';

import type { CheckRow } from '../../hooks/useEntityScorecard';
import { CategoryFilters } from './CategoryFilters';
import { FailingChecks } from './FailingChecks';
import { OverallCard } from './OverallCard';

interface SummaryCardsProps {
  rows: CheckRow[];
  isLoading: boolean;
}

export const SummaryCards = ({ rows, isLoading }: SummaryCardsProps) => {
  const total = rows.length;
  const passing = rows.filter(r => r.snapshot?.result === 'passed').length;
  const pct = total > 0 ? Math.round((passing / total) * 100) : 0;

  const lastRunIso = rows
    .map(r => r.snapshot?.snapshotTime)
    .filter(Boolean)
    .sort()
    .at(-1);

  const failing = rows.filter(r => r.snapshot?.result === 'failed').length;

  const severityCounts = new Map<string, number>();
  for (const row of rows) {
    if (row.snapshot?.result === 'failed') {
      severityCounts.set(
        row.check.severity,
        (severityCounts.get(row.check.severity) ?? 0) + 1,
      );
    }
  }

  const categoryStats = new Map<
    string,
    { passing: number; failing: number; total: number }
  >();
  for (const row of rows) {
    const cat = row.check.category;
    const entry = categoryStats.get(cat) ?? {
      passing: 0,
      failing: 0,
      total: 0,
    };
    entry.total++;
    if (row.snapshot?.result === 'passed') entry.passing++;
    else if (row.snapshot?.result === 'failed') entry.failing++;
    categoryStats.set(cat, entry);
  }

  if (isLoading) {
    return (
      <Flex
        gap="4"
        align="stretch"
        style={{ marginBottom: 'var(--bui-space-4)', flexWrap: 'wrap' }}
      >
        <Skeleton height={120} style={{ flex: '1 1 200px' }} />
        <Skeleton height={120} style={{ flex: '1 1 200px' }} />
      </Flex>
    );
  }

  if (total === 0) return null;

  return (
    <Flex
      direction="column"
      gap="4"
      style={{ marginBottom: 'var(--bui-space-4)' }}
    >
      <Flex gap="4" align="stretch" style={{ flexWrap: 'wrap' }}>
        <OverallCard
          pct={pct}
          passing={passing}
          failing={failing}
          total={total}
          lastRunIso={lastRunIso}
        />
        <FailingChecks failing={failing} severityCounts={severityCounts} />
      </Flex>
      <CategoryFilters categoryStats={categoryStats} />
    </Flex>
  );
};
